import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  Restaurant,
  Handshake,
  PointOfSale,
  CloudDownload,
  HelpOutline,
  Refresh,
  Paid,
} from '@mui/icons-material';
import TabDescription from './TabDescription';
import { useRegionContext } from '../../region/RegionContext';
import { queryAllRegions } from '../../utils/queryAllRegions';

var EVENT_CONFIG = {
  venue_signup: { label: 'Visible', color: 'primary', icon: Visibility },
  venue_claimed: { label: 'Claimed', color: 'success', icon: Handshake },
  pos_connected: { label: 'POS Connected', color: 'info', icon: PointOfSale },
  pos_requested: { label: 'POS Requested', color: 'warning', icon: HelpOutline },
  venue_imported: { label: 'Imported', color: 'default', icon: CloudDownload },
  stripe_connected: { label: 'Piatto Partner', color: 'success', icon: Paid },
};

function relativeTime(timestamp) {
  if (!timestamp) return '';
  var date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  var now = new Date();
  var diffMs = now - date;
  var diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return diffMins + 'm ago';
  var diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return diffHours + 'h ago';
  var diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return diffDays + 'd ago';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function eventDetail(event) {
  var meta = event.metadata || {};
  switch (event.type) {
    case 'venue_signup':
      return meta.source ? 'Source: ' + meta.source : null;
    case 'venue_claimed':
      return [
        meta.ownerName,
        meta.ownerEmail ? '(' + meta.ownerEmail + ')' : null,
      ].filter(Boolean).join(' ') || null;
    case 'pos_connected':
      return [
        meta.platform,
        meta.locationName ? '— ' + meta.locationName : null,
        meta.connectedByEmail ? '(' + meta.connectedByEmail + ')' : null,
      ].filter(Boolean).join(' ') || null;
    case 'pos_requested':
      return [
        meta.posName || meta.posType,
        meta.userEmail ? '(' + meta.userEmail + ')' : null,
      ].filter(Boolean).join(' ') || null;
    case 'stripe_connected':
      return meta.ownerEmail ? '(' + meta.ownerEmail + ')' : null;
    default:
      return null;
  }
}

var PAGE_SIZE = 25;

var ActivityFeedTab = function () {
  var { db, isGlobal, allRegionDbs } = useRegionContext();
  var [events, setEvents] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [page, setPage] = useState(0);

  useEffect(function () {
    if (db) loadEvents();
  }, [db, isGlobal, allRegionDbs]);

  var loadEvents = async function () {
    try {
      setLoading(true);
      setError(null);
      setPage(0);

      var activityQuery = function (regionDb) {
        return regionDb.collection('admin_activity')
          .orderBy('timestamp', 'desc')
          .limit(200);
      };

      var data;
      if (isGlobal && allRegionDbs) {
        data = await queryAllRegions(allRegionDbs, null, activityQuery);
        data.sort(function (a, b) {
          var aTime = a.timestamp?.toDate?.()?.getTime?.() || 0;
          var bTime = b.timestamp?.toDate?.()?.getTime?.() || 0;
          return bTime - aTime;
        });
      } else {
        var snapshot = await activityQuery(db).get();
        data = snapshot.docs.map(function (doc) { return { id: doc.id, ...doc.data() }; });
      }

      setEvents(data);
    } catch (err) {
      console.error('Error loading activity feed:', err);
      setError('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading activity feed...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <TabDescription>
        Chronological feed of onboarding activity — venue visibility, claims, POS connections, and Stripe partnerships.
        "Visible" means the venue appeared on Piatto (via import or sign-up). "Piatto Partner" means the venue completed Stripe onboarding. Claim events also trigger a separate email.
      </TabDescription>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadEvents}
        >
          Refresh
        </Button>
      </Box>

      {events.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              No activity events yet. Events will appear here as venues sign up, claim, and connect POS systems.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button variant="outlined" size="small" disabled={page === 0} onClick={function () { setPage(function (p) { return p - 1; }); }}>
            Previous
          </Button>
          <Typography variant="body2">
            Page {page + 1} of {Math.ceil(events.length / PAGE_SIZE)} ({events.length} total)
          </Typography>
          <Button variant="outlined" size="small" disabled={(page + 1) * PAGE_SIZE >= events.length} onClick={function () { setPage(function (p) { return p + 1; }); }}>
            Next
          </Button>
        </Box>
        <List disablePadding>
          {events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(function (event, i) {
            var config = EVENT_CONFIG[event.type] || EVENT_CONFIG.venue_imported;
            var IconComponent = config.icon;
            var detail = eventDetail(event);

            return (
              <ListItem
                key={event.id || i}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                  px: 2,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ mr: 2, mt: 0.5, color: 'text.secondary' }}>
                  <IconComponent fontSize="small" />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      label={config.label}
                      color={config.color}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {event.businessName || event.businessId}
                    </Typography>
                    {event._region && (
                      <Chip label={event._region} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    )}
                  </Box>

                  {event.businessAddress && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {event.businessAddress}
                    </Typography>
                  )}

                  {detail && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {detail}
                    </Typography>
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap', mt: 0.5 }}>
                  {relativeTime(event.timestamp)}
                </Typography>
              </ListItem>
            );
          })}
        </List>
        </>
      )}
    </Box>
  );
};

export default ActivityFeedTab;
